// weather.js — Open-Meteo client. No API key needed.
// Geolocation first; ZIP code fallback (US only via geocoding-api.open-meteo.com).
//
// Cached in localStorage for 30 minutes — Daily tab + Coffee tab can both read
// without hitting the network on every render.

const CACHE_KEY = "cucina_v1:weather_cache";
const ZIP_KEY = "cucina_v1:weather_zip";
const LOCATION_KEY = "cucina_v1:weather_location";
const TTL_MS = 30 * 60 * 1000;

export function getStoredZip() {
  try { return localStorage.getItem(ZIP_KEY) || ""; } catch { return ""; }
}

export function setStoredZip(zip) {
  try {
    if (zip) localStorage.setItem(ZIP_KEY, String(zip).trim());
    else localStorage.removeItem(ZIP_KEY);
  } catch {}
}

function getStoredLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setStoredLocation(loc) {
  try {
    if (loc) localStorage.setItem(LOCATION_KEY, JSON.stringify(loc));
    else localStorage.removeItem(LOCATION_KEY);
  } catch {}
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (Date.now() - obj.t > TTL_MS) return null;
    return obj.data;
  } catch { return null; }
}

function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), data })); } catch {}
}

async function geocodeZip(zip) {
  const clean = String(zip || "").trim();
  if (!clean) throw new Error("ZIP required");
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(clean)}&count=1&countryCode=US&format=json`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("Geocoding failed");
  const j = await r.json();
  const hit = j?.results?.[0];
  if (!hit) throw new Error(`ZIP ${clean} not recognized`);
  return {
    lat: hit.latitude,
    lon: hit.longitude,
    label: hit.name + (hit.admin1 ? `, ${hit.admin1}` : ""),
    zip: clean,
  };
}

function geolocateBrowser() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation unsupported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        label: "Current location",
      }),
      (err) => reject(err),
      { timeout: 7000, maximumAge: 60 * 60 * 1000 }
    );
  });
}

async function fetchForecast({ lat, lon }) {
  const url = `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,apparent_temperature,is_day,weather_code,relative_humidity_2m,wind_speed_10m,uv_index` +
    `&hourly=temperature_2m,precipitation_probability,weather_code` +
    `&daily=temperature_2m_max,temperature_2m_min,sunrise,sunset` +
    `&temperature_unit=fahrenheit&windspeed_unit=mph&forecast_days=2&timezone=auto`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("Forecast failed");
  return r.json();
}

/**
 * Resolve location and fetch weather.
 * Tries: geolocation → stored ZIP → throws.
 *
 * Returns:
 *   { temp_f, feels_f, code, condition, is_day, location: {label, lat, lon},
 *     today: {high_f, low_f, sunrise, sunset}, peak_3h_precip_pct, raining_now,
 *     raw: <full response> }
 */
export async function getWeather({ preferZip = false } = {}) {
  const cached = readCache();
  if (cached) return cached;

  let location = getStoredLocation();

  if (!location) {
    if (!preferZip) {
      try { location = await geolocateBrowser(); }
      catch { /* fall back to ZIP */ }
    }
    if (!location) {
      const zip = getStoredZip();
      if (zip) location = await geocodeZip(zip);
    }
  }
  if (!location) throw new Error("No location — set ZIP in Settings or grant location permission");

  setStoredLocation(location);

  const data = await fetchForecast(location);
  const result = normalizeWeather(data, location);
  writeCache(result);
  return result;
}

/**
 * Force re-geocode of a ZIP. Used when user changes their ZIP in Settings.
 */
export async function refreshZip(zip) {
  setStoredZip(zip);
  setStoredLocation(null);
  try { localStorage.removeItem(CACHE_KEY); } catch {}
  return getWeather({ preferZip: true });
}

function normalizeWeather(raw, location) {
  const cur = raw.current || {};
  const daily = raw.daily || {};
  const hourly = raw.hourly || {};

  // Find current hour index in hourly arrays
  let hourIdx = 0;
  if (hourly.time?.length) {
    const nowMs = Date.now();
    for (let i = 0; i < hourly.time.length; i++) {
      const t = new Date(hourly.time[i]).getTime();
      if (t <= nowMs) hourIdx = i;
    }
  }

  // Peak precip prob over next 3 hours
  let peak = 0;
  for (let i = 0; i < 3; i++) {
    const p = hourly.precipitation_probability?.[hourIdx + i];
    if (p != null) peak = Math.max(peak, +p);
  }

  return {
    temp_f: cur.temperature_2m != null ? Math.round(cur.temperature_2m) : null,
    feels_f: cur.apparent_temperature != null ? Math.round(cur.apparent_temperature) : null,
    code: cur.weather_code,
    condition: conditionText(cur.weather_code),
    emoji: weatherEmoji(cur.weather_code, !!cur.is_day),
    is_day: !!cur.is_day,
    humidity: cur.relative_humidity_2m,
    wind_mph: cur.wind_speed_10m != null ? Math.round(cur.wind_speed_10m) : null,
    uv: cur.uv_index,
    location,
    today: {
      high_f: daily.temperature_2m_max?.[0] != null ? Math.round(daily.temperature_2m_max[0]) : null,
      low_f:  daily.temperature_2m_min?.[0] != null ? Math.round(daily.temperature_2m_min[0]) : null,
      sunrise: daily.sunrise?.[0],
      sunset:  daily.sunset?.[0],
    },
    peak_3h_precip_pct: Math.round(peak),
    raining_now: peak >= 35 || isRainCode(cur.weather_code),
    fetched_at: Date.now(),
  };
}

function isRainCode(code) {
  if (code == null) return false;
  return (code >= 51 && code <= 67) || (code >= 80 && code <= 82) || (code >= 95 && code <= 99);
}

function weatherEmoji(code, isDay) {
  if (code === 0) return isDay ? "☀️" : "🌙";
  if ([1, 2].includes(code)) return isDay ? "🌤️" : "☁️";
  if (code === 3) return "☁️";
  if ([45, 48].includes(code)) return "🌫️";
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "🌧️";
  if (code >= 71 && code <= 77) return "🌨️";
  if (code >= 95 && code <= 99) return "⛈️";
  return isDay ? "🌤️" : "🌙";
}

function conditionText(code) {
  if (code === 0) return "Clear";
  if ([1, 2].includes(code)) return "Partly cloudy";
  if (code === 3) return "Cloudy";
  if ([45, 48].includes(code)) return "Foggy";
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "Rainy";
  if (code >= 71 && code <= 77) return "Snowy";
  if (code >= 95 && code <= 99) return "Storm";
  return "Cloudy";
}

// Weather → brew-mode bias for the morning ritual.
//   Below 50°F:  hot + bold suggested
//   50–80°F:     no bias, balanced
//   Above 80°F:  iced suggested
//   Above 90°F:  cold-brew strongly suggested
//
// Returns { suggested_mode: "hot"|"iced"|"cold", suggested_profile_id, reason }
export function brewSuggestionFromWeather(weather) {
  if (!weather || weather.temp_f == null) return null;
  const t = weather.feels_f ?? weather.temp_f;

  if (t >= 90) return {
    suggested_mode: "cold",
    suggested_profile_id: "cold-brew",
    reason: `${t}°F outside — cold brew weather`,
  };
  if (t >= 80) return {
    suggested_mode: "iced",
    suggested_profile_id: "iced",
    reason: `${t}°F — try Japanese-style iced`,
  };
  if (t < 50) return {
    suggested_mode: "hot",
    suggested_profile_id: "bold",
    reason: `${t}°F — bold roast for the cold`,
  };
  return {
    suggested_mode: "hot",
    suggested_profile_id: null,
    reason: `${t}°F — middle of the road, your call`,
  };
}

// Time-of-day phase, used for greetings + Daily tab mode.
export function timeOfDay(now = new Date()) {
  const h = now.getHours();
  if (h < 5)  return "late_night";
  if (h < 11) return "morning";
  if (h < 14) return "midday";
  if (h < 17) return "afternoon";
  if (h < 21) return "evening";
  return "night";
}

export function greetingFor(phase, weather) {
  const day = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const condition = weather?.condition?.toLowerCase() || "";
  const t = weather?.temp_f;

  const tempPhrase = t != null
    ? (t < 40 ? `${t}° and cold` : t < 60 ? `${t}° and crisp` : t < 75 ? `${t}° and pleasant` : t < 85 ? `${t}° and warm` : `${t}° and hot`)
    : "";
  const conditionPhrase = condition && condition !== "clear" ? `, ${condition}` : "";

  const map = {
    late_night: `${day} night${tempPhrase ? `, ${tempPhrase}` : ""}`,
    morning:    `${day} morning${tempPhrase ? `, ${tempPhrase}${conditionPhrase}` : ""}`,
    midday:     `${day} ${tempPhrase ? `${tempPhrase}${conditionPhrase}` : ""}`.trim(),
    afternoon:  `${day} afternoon${tempPhrase ? `, ${tempPhrase}${conditionPhrase}` : ""}`,
    evening:    `${day} evening${tempPhrase ? `, ${tempPhrase}` : ""}`,
    night:      `${day} night${tempPhrase ? `, ${tempPhrase}` : ""}`,
  };
  return map[phase] || `${day}`;
}
